import React, { useState, useEffect } from "react";
import {
  SimpleGrid,
  Box,
  VStack,
  Spacer,
  Show,
  Skeleton,
  Heading,
  Button,
  Flex,
  useToast,
  useColorModeValue,
} from "@chakra-ui/react";
import { Link, useParams } from "react-router-dom";
import { Trans } from "@lingui/react/macro";

import Navbar from "../../components/Navbar";
import useAxios from "../../utils/useAxios";
import DataDumpValve from "../../components/DeviceComponents/DataDumpValve";
import { DeviceMap } from "../../components/DeviceComponents/DeviceMap";
import DevicePlotValve from "../../components/DeviceComponents/DevicePlotValve";

import IrrigationControllerTile from "../../components/IrrigationComponents/IrrigationControllerTile";
import { IValveData } from "../../types/alarmInterface";
import { IrrigationOverviewType } from "../../types/irrigationInterface";

type ControllerListItem = IrrigationOverviewType;

const ValveViewer: React.FC = () => {
  const api = useAxios();
  const toast = useToast();
  const { id } = useParams<{ id: string }>();

  const [ctrl, setCtrl] = useState<ControllerListItem | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const boxColor = useColorModeValue("white", "gray.900");
  const headingColor = useColorModeValue("black", "gray.200");

  useEffect(() => {
    const fetchControllers = async () => {
      try {
        const { data } = await api.get<IrrigationOverviewType>(
          `agtech/irrigation/by-device/${id}/`
        );
        setCtrl(data);

        // Fetch pending command status
        if (data?.id) {
          try {
            const pendingResponse = await api.get(`/agtech/irrigation/${data.id}/pending-command/`);
            setCtrl((prev) =>
              prev ? { ...prev, pending_valve_command: pendingResponse.data } : null
            );
          } catch (err) {
            console.log("Error fetching pending command status");
          }
        }
      } catch (err: any) {
        toast({
          title: <Trans>Error loading controller list</Trans>,
          description: err.message,
          status: "error",
          duration: 9000,
          isClosable: true,
        });
      } finally {
        setIsLoading(false);
      }
    };
    fetchControllers();
  }, []);

  const device = ctrl?.controller_data;

  const valveState = ctrl?.current_valve_state?.state ?? ctrl?.state;
  const valveData: IValveData | null = ctrl
    ? {
        last_seen: ctrl.sensor_data?.data?.timestamp ?? undefined,
        current_state:
          valveState !== null && valveState !== undefined
            ? valveState
              ? "Open"
              : "Closed"
            : undefined,
        battery_voltage: device?.data?.battery_voltage,
        air_temperature: device?.data?.air_temperature,
        humidity: device?.data?.humidity,
      }
    : null;

  return (
    <Navbar>
      <VStack mt={2} align="stretch" spacing={4} p={4}>
        <SimpleGrid minChildWidth="350px" spacing="2">
          <Box p={5} bg={boxColor} boxShadow="md" borderRadius="md">
            {isLoading ? (
              <Skeleton height="24px" mb={4} />
            ) : (
              <Flex align="center" mb={4}>
                <Heading as="h2" size="lg" color={headingColor}>
                  {device?.device_name ?? <Trans>Unnamed Valve</Trans>}
                </Heading>
                <Spacer />
                <Button
                  as={Link}
                  to={`/devices/${id}/edit`}
                  size="sm"
                  variant="outline"
                  colorScheme="teal"
                >
                  <Trans>Edit</Trans>
                </Button>
              </Flex>
            )}

            <Heading size="sm" mb={2}>
              <Trans>Current Data:</Trans>
            </Heading>
            {isLoading ? <Skeleton height="100px" /> : <DataDumpValve valveData={valveData} />}
          </Box>

          <Show above="lg">
            <Box p={5} bg={boxColor} boxShadow="md" borderRadius="md" h="300px">
              {isLoading ? <Skeleton height="100%" /> : <DeviceMap device={device} />}
            </Box>
          </Show>
        </SimpleGrid>

        <Show below="lg">
          <Box p={5} bg={boxColor} boxShadow="md" borderRadius="md" h="300px">
            {isLoading ? <Skeleton height="100%" /> : <DeviceMap device={device} />}
          </Box>
        </Show>

        <Flex gap={4} direction={{ base: "column", lg: "row" }} align="stretch">
          <Box flex={{ base: "0 1 auto", lg: "1 1 0" }} pr={{ base: 0, lg: 4 }} w="100%" minW={0}>
            {isLoading ? (
              <Skeleton height="300px" />
            ) : (
              ctrl && <IrrigationControllerTile irrigation={ctrl} />
            )}
          </Box>
          <Box
            flex={{ base: "0 1 auto", lg: "2 1 0" }}
            p={5}
            bg={boxColor}
            boxShadow="md"
            borderRadius="md"
            w="100%"
            minW={0}
            maxW={{ base: "100%", xl: "1300px" }}
            overflowX="auto"
          >
            <Box w="100%" minW={0}>
              <DevicePlotValve />
            </Box>
          </Box>
        </Flex>
      </VStack>
    </Navbar>
  );
};

export default ValveViewer;
